/**
 * CoSparks design tokens. Dark, crisp, "instrument panel" look: deep navy space, electric blue
 * signal colour, a warm spark accent, frosted glass panels and near-square corners.
 * Every colour used by the app should come from here (or the MUI theme built from it).
 */
export const color = {
    void: '#05040f',        // page background, deepest
    space: '#0a0820',       // raised background
    panel: 'rgba(18, 16, 48, 0.62)',   // glass panel fill
    panelSolid: '#121030',
    panelHover: 'rgba(30, 27, 74, 0.72)',
    line: 'rgba(140, 170, 255, 0.16)', // hairlines and panel outlines
    lineStrong: 'rgba(140, 170, 255, 0.32)',
    wash: 'rgba(140, 170, 255, 0.05)',   // faint fill: table stripes

    signal: '#2fb0ff',      // primary: electric blue
    signalSoft: 'rgba(47, 176, 255, 0.14)',
    spark: '#ffb12e',       // accent: the spark
    sparkSoft: 'rgba(255, 177, 46, 0.14)',
    violet: '#6b4bff',      // background glow
    rose: '#ff7ad1',        // background glow

    text: '#eef2ff',
    textMuted: 'rgba(214, 222, 255, 0.64)',
    textFaint: 'rgba(214, 222, 255, 0.40)',

    success: '#3ddc97',
    warning: '#ffb12e',
    danger: '#ff5d6c',
} as const;

export const font = {
    display: '"Space Grotesk Variable", "Inter Variable", system-ui, sans-serif',
    body: '"Inter Variable", system-ui, -apple-system, "Segoe UI", sans-serif',
    mono: '"JetBrains Mono", ui-monospace, "Cascadia Mono", Consolas, monospace',
} as const;

/** Small mono caps, for labels and table headings: `sx={{ ...monoLabel(11), color: color.textFaint }}`. */
export const monoLabel = (fontSize: number | string, letterSpacing = '0.12em') =>
    ({ fontFamily: font.mono, fontSize, letterSpacing, textTransform: 'uppercase' }) as const;

/** A slow fade in and out, for "live" dots; still for people who ask for less motion. */
export const livePulse = (seconds = 1.6) => ({
    '@keyframes cs-live-pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.35 } },
    animation: `cs-live-pulse ${seconds}s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/** Near-square corners, like the reference: crisp, technical. */
export const radius = {
    xs: 2,
    sm: 4,
    md: 6,
} as const;

/** Motion: one expressive curve for entrances, one quick curve for UI feedback. */
export const ease = {
    out: [0.16, 1, 0.3, 1] as const,          // expo-out: reveals, page transitions
    inOut: [0.62, 0.05, 0.01, 0.99] as const, // dramatic in-out: large moves
    snap: [0.165, 0.84, 0.44, 1] as const,    // hover/press feedback
};

export const cssEase = {
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',
    snap: 'cubic-bezier(0.165, 0.84, 0.44, 1)',
} as const;

export const duration = {
    fast: 0.18,
    base: 0.32,
    slow: 0.7,
    /** Page entrance on navigation: quick, so moving around never feels slow. */
    page: 0.35,
    reveal: 0.9,
} as const;

export const glass = {
    background: color.panel,
    backdropFilter: 'blur(18px) saturate(140%)',
    WebkitBackdropFilter: 'blur(18px) saturate(140%)',
    border: `1px solid ${color.line}`,
} as const;
